import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import {
  getCurrentClientConfig,
  applyClientConfig,
  type ClientBrandingConfig,
} from '@/config/clients';
import { useFavicon } from '@/hooks/useFavicon';

/**
 * Client Configuration Context
 * Provides client-specific branding configuration throughout the app
 */
interface ClientConfigContextType {
  clientConfig: ClientBrandingConfig;
}

const ClientConfigContext = createContext<ClientConfigContextType | undefined>(
  undefined
);

/**
 * Custom hook to use the ClientConfigContext
 * @returns Client configuration context
 */
export const useClientConfig = (): ClientConfigContextType => {
  const context = useContext(ClientConfigContext);
  if (!context) {
    throw new Error(
      'useClientConfig must be used within a ClientConfigProvider'
    );
  }
  return context;
};

/**
 * Provider props
 */
interface ClientConfigProviderProps {
  children: ReactNode;
}

/**
 * Client Configuration Provider
 * Initializes and provides client configuration throughout the app
 */
export const ClientConfigProvider: React.FC<ClientConfigProviderProps> = ({
  children,
}) => {
  const clientConfig = getCurrentClientConfig();

  // Use favicon hook to manage favicon within this MFE only
  useFavicon(clientConfig.faviconSrc);

  useEffect(() => {
    // Apply client configuration when provider mounts
    applyClientConfig(clientConfig);

    console.log('Client configuration applied:', {
      clientId: clientConfig.clientId,
      clientName: clientConfig.clientName,
      faviconSrc: clientConfig.faviconSrc,
    });
  }, [clientConfig]);

  const value: ClientConfigContextType = {
    clientConfig,
  };

  return (
    <ClientConfigContext.Provider value={value}>
      {children}
    </ClientConfigContext.Provider>
  );
};

export default ClientConfigProvider;
